import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useState } from "react";
import DateRange from "../../InvoiceReport/ExpenseGroupOrSubgroupTotalsByDate/DateRange";
import { presetDateRangeOptions } from "../../../../../utilities/dateRangeUtils";
import { stockCountReportTypes, comparisonReportDisplayTypes } from "../../../../../clients/inventoryObjects";
import inventoryClient from "../../../../../clients/inventoryClient";
import utilFunctions from "../../../../../utilities/utilFunctions";
import { useMutation } from "react-query";
import ReportingFooter from "../../ReportingFooter";
import TemplateFilters from "./TemplateFilters";
import DisplayType from "./DisplayType";
import useStockCountTemplates from "../../../../../hooks/useStockCountTemplates";
import IncludeVendorToggle from "./IncludeVendorToggle";

const dateSelectionFormats = {
  presetOption: 0,
  customRange: 1,
};

const displayTypeOptions = [
  { key: "Quantity", value: comparisonReportDisplayTypes.quantity },
  { key: "Value on Hands", value: comparisonReportDisplayTypes.valueOnHands },
];

const ComparisonByDate = ({
  establishmentId,
  establishmentTimezone,
  setAlertSuccess,
  setErrorMessage,
}) => {
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [customStartDateDisplay, setCustomStartDateDisplay] = useState(null);
  const [customEndDateDisplay, setCustomEndDateDisplay] = useState(null);
  const [selectedPresetDateRange, setSelectedPresetDateRange] = useState(
    presetDateRangeOptions[0].value
  );
  const [dateSelectionFormat, setDateSelectionFormat] = useState(
    dateSelectionFormats.presetOption
  );
  const [isUsingTemplateFilter, toggleIsUsingTemplateFilter] = useState(false);
  const [templateFilters, setTemplateFilters] = useState([]);
  const [selectedDisplayType, setSelectedDisplayType] = useState(
    comparisonReportDisplayTypes.quantity
  );
  const [includeVendorColumn, toggleIncludeVendorColumn] = useState(false);

  const { isLoading: isLoadingTemplates, data: templatesData } =
    useStockCountTemplates(establishmentId);

  const generateStockCountReportMutation = useMutation(
    inventoryClient.generateStockCountReport,
    {
      onSuccess: (response) => {
        if (response.status === 204) {
          setErrorMessage(
            "No data found for the selected date range. Please adjust your filters and try again."
          );
        } else {
          utilFunctions.downloadFile(
            response?.data?.report?.stockCountReportMetadata?.s3PresignedUrl
          );
          setAlertSuccess(
            "Report generated successfully, your download should have started."
          );
        }
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.messages?.[0] ||
          "Report generation failed!";
        setErrorMessage(errorMessage);
      },
    }
  );

  const handleGenerateReport = () => {
    const payload = {
      establishmentId: establishmentId,
      reportType: stockCountReportTypes.comparisonByDate,
      comparisonByDateReportFilters: {
        templateIds: isUsingTemplateFilter
          ? templateFilters.map((t) => t.id)
          : [],
        displayType: selectedDisplayType,
        includeVendorColumn: includeVendorColumn,
      },
    };

    if (dateSelectionFormat === dateSelectionFormats.presetOption) {
      const dateRange = presetDateRangeOptions
        .find((x) => x.value === selectedPresetDateRange)
        .func(establishmentTimezone);
      payload.comparisonByDateReportFilters.startDate = dateRange.start;
      payload.comparisonByDateReportFilters.endDate = dateRange.end;
    } else {
      payload.comparisonByDateReportFilters.startDate = customStartDate;
      payload.comparisonByDateReportFilters.endDate = customEndDate;
    }

    generateStockCountReportMutation.mutate(payload);
  };

  const isGenerateDisabled = () => {
    if (dateSelectionFormat === dateSelectionFormats.customRange) {
      if (!customStartDate || !customEndDate) {
        return true;
      }
      if (customEndDate < customStartDate) {
        return true;
      }
    }
    return false;
  };

  if (isLoadingTemplates) {
    return (
      <Box
        sx={{
          height: 250,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ marginBottom: 12 }}>
      <Box sx={{ marginTop: 8 }}>
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              height: "fit-content",
              width: "70%",
              borderRadius: 2,
              backgroundColor: (theme) => theme.palette.terrain[200],
              border: "1px solid",
              borderColor: (theme) => theme.palette.terrain[300],
              padding: 2,
            }}
          >
            <Typography variant="body2">
              Generate a comparison report showing product format quantities
              across multiple stock counts within a selected date range. Each
              column represents a stock count, and each row represents a product
              format that appeared in at least one count.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ marginTop: 8 }}>
        <DateRange
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          selectedPresetDateRange={selectedPresetDateRange}
          presetDateRangeOptions={presetDateRangeOptions}
          setSelectedPresetDateRange={setSelectedPresetDateRange}
          dateSelectionFormat={dateSelectionFormat}
          setDateSelectionFormat={setDateSelectionFormat}
          dateSelectionFormats={dateSelectionFormats}
          establishmentTimezone={establishmentTimezone}
          setCustomStartDateDisplay={setCustomStartDateDisplay}
          customStartDateDisplay={customStartDateDisplay}
          customEndDateDisplay={customEndDateDisplay}
          setCustomEndDateDisplay={setCustomEndDateDisplay}
        />
      </Box>
      </Box>
      <Box sx={{ marginTop: 8 }}>
        <DisplayType
          displayTypeOptions={displayTypeOptions}
          selectedDisplayType={selectedDisplayType}
          setSelectedDisplayType={setSelectedDisplayType}
        />
      </Box>
      <Box sx={{ marginTop: 8 }}>
        <IncludeVendorToggle
          includeVendorColumn={includeVendorColumn}
          toggleIncludeVendorColumn={toggleIncludeVendorColumn}
        />
      </Box>
      <Box sx={{ marginTop: 8 }}>
        <TemplateFilters
          isUsingTemplateFilter={isUsingTemplateFilter}
          toggleIsUsingTemplateFilter={toggleIsUsingTemplateFilter}
          templateFilters={templateFilters}
          setTemplateFilters={setTemplateFilters}
          templates={templatesData?.countTemplates || []}
        />
      </Box>

      <ReportingFooter
        submit={handleGenerateReport}
        buttonText="Create Report"
        isLoading={generateStockCountReportMutation.isLoading}
        disableButton={isGenerateDisabled()}
      />
    </Box>
  );
};

export default ComparisonByDate;
